import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { BRAND, NAV } from "@/lib/brand";

export default function Header({ onOpenInquiry }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    if (!isHome) {
      navigate(`/#${id}`);
      return;
    }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b border-zinc-200"
          : "bg-white/0"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between h-20 md:h-24">
        <a
          href="/"
          data-testid="header-logo"
          className="flex items-center gap-3 group"
          onClick={(e) => {
            if (isHome) {
              e.preventDefault();
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
        >
          <img
            src={BRAND.logo}
            alt="Thecoventures — vending machine solutions"
            width={BRAND.logoWidth}
            height={BRAND.logoHeight}
            fetchpriority="high"
            decoding="async"
            onError={(e) => {
              // Fallback to the hosted CDN copy if the local /logo.png is missing
              // for any reason (e.g. cache-buster after redeploy).
              if (e.currentTarget.src !== BRAND.logoFallback) {
                e.currentTarget.src = BRAND.logoFallback;
              }
            }}
            className="h-14 md:h-16 lg:h-[72px] w-auto object-contain"
          />
        </a>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((item) => (
            <button
              key={item.id}
              data-testid={`nav-${item.id}`}
              onClick={() => go(item.id)}
              className="px-4 py-2 text-sm text-zinc-700 hover:text-zinc-950 rounded-full hover:bg-zinc-100 transition"
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="header-cta-quote"
            onClick={onOpenInquiry}
            className="hidden md:inline-flex items-center gap-2 bg-zinc-950 hover:bg-zinc-800 text-white font-semibold rounded-full px-5 py-2.5 text-sm transition-colors"
          >
            Get a Free Quote
            <ArrowUpRight className="w-4 h-4" />
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((s) => !s)}
            aria-label="menu"
            className="md:hidden w-10 h-10 rounded-full border border-zinc-200 bg-white text-zinc-900 flex items-center justify-center"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-white border-b border-zinc-200"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV.map((item) => (
                <button
                  key={item.id}
                  data-testid={`mobile-nav-${item.id}`}
                  onClick={() => go(item.id)}
                  className="text-left py-3 px-3 rounded-xl text-zinc-800 hover:bg-zinc-100"
                >
                  {item.label}
                </button>
              ))}
              <button
                data-testid="mobile-cta-quote"
                onClick={() => {
                  setOpen(false);
                  onOpenInquiry();
                }}
                className="mt-2 inline-flex items-center justify-center gap-2 bg-zinc-950 text-white font-semibold rounded-full px-5 py-3"
              >
                Get a Free Quote <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
